import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import type { SamplePointCollection } from "@domain/Geo/sample";
import { Geo } from "@rogoag/geo";

export const MIN_SAFE_DISTANCE_METERS = 5;

/**
 * Check if a point is inside a polygon AND at least `safeDistanceMeters`
 * away from the nearest boundary edge. Handles Polygon and MultiPolygon.
 */
export function isPointSafeInsidePolygon(
	coord: Position,
	poly: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): boolean {
	// Malformed/missing coordinates (e.g. from a bad upload) can't be verified safe.
	if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return false;
	// coord is [lon, lat] per GeoJSON spec
	return Geo.isPointSafeInsidePolygon(poly, coord[1], coord[0], safeDistanceMeters);
}

/**
 * Find sample IDs that are NOT safe inside the boundary.
 * A point is unsafe if it's outside the boundary or within `safeDistanceMeters` of the edge.
 */
export function findUnsafeSampleIds(
	points: SamplePointCollection,
	boundary: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): Set<string> {
	const unsafe = new Set<string>();
	for (const f of points.features) {
		if (f.geometry.type !== "Point") continue;
		if (!isPointSafeInsidePolygon(f.geometry.coordinates, boundary, safeDistanceMeters)) {
			unsafe.add(f.properties.sample_id);
		}
	}
	return unsafe;
}

/**
 * Find sample IDs that are NOT safe inside any boundary feature in the FC.
 * A point is safe if it's safe in ANY boundary feature (union semantics).
 */
export function findUnsafeSampleIdsInFC(
	points: SamplePointCollection,
	boundaryFC: FeatureCollection,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): Set<string> {
	const boundaryFeatures = boundaryFC.features.filter(
		(f) => f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon",
	) as Feature<Polygon | MultiPolygon>[];
	if (boundaryFeatures.length === 0) return new Set();

	let result: Set<string> | null = null;
	for (const bf of boundaryFeatures) {
		const unsafeInThis = findUnsafeSampleIds(points, bf, safeDistanceMeters);
		if (!result) {
			result = unsafeInThis;
		} else {
			// Intersect: keep only IDs unsafe in ALL boundary features
			for (const id of result) {
				if (!unsafeInThis.has(id)) result.delete(id);
			}
		}
	}
	return result ?? new Set();
}
